import React, { useCallback, useEffect, useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { useWeb3React } from '@web3-react/core';
import ConnectButton from '../../../components/ConnectButton';

import StakeNav from '../../../components/StakeNav';
import useGenesisNoundleBalance from '../../../hooks/useGenesisNoundleBalance';
import useWrappedNoundles from '../../../hooks/useWrappedNoundles';
import useNoundleStakingPool from '../../../hooks/useNoundleStakingPool';
import StakedNoundlesPanel from '../../../components/StakedNoundlesPanel';
import UnstakedNoundlesPanel from '../../../components/UnstakedNoundlesPanel';

const StakeNoundles = () => {
  const { active, account } = useWeb3React();
  const legacyBalance = useGenesisNoundleBalance();

  const wrappedNoundles = useWrappedNoundles();
  const stakingPool = useNoundleStakingPool();

  const [approved, setApproved] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let ignore = false;
    setApproved(false);
    if (account && wrappedNoundles && stakingPool) {
      const fetchApproval = () => {
        wrappedNoundles
          .isApprovedForAll(account, stakingPool.address)
          .then((approved) => {
            if (!ignore) {
              setApproved(approved);
            }
          });
      };

      fetchApproval();

      const approval = wrappedNoundles.filters.ApprovalForAll(
        account,
        stakingPool.address
      );
      wrappedNoundles.on(approval, fetchApproval);
      return () => {
        wrappedNoundles.off(approval, fetchApproval);
        ignore = true;
      };
    }
  }, [account, wrappedNoundles, stakingPool]);

  const approve = useCallback(async () => {
    if (wrappedNoundles && stakingPool) {
      try {
        await wrappedNoundles
          .setApprovalForAll(stakingPool.address, true)
          .then((tx) => {
            setLoading(true);
            return tx.wait();
          });
      } catch (e) {
        if (e.code != 4001) {
          // 4001 means the user rejected the transaction in metamask
          console.log(e);
        }
      }
      setLoading(false);
    }
  }, [wrappedNoundles, stakingPool]);

  if (!active) {
    return (
      <div className="pink-gradient">
        <div className="container py-5 text-center">
          <p className="lead">Please connect your wallet</p>
          <ConnectButton />
        </div>
      </div>
    );
  }

  if (!approved) {
    return (
      <>
        <Head>
          <title>Stake Noundles | Noundles</title>
        </Head>

        <div className="pink-gradient">
          <div className="container py-5 text-center">
            <StakeNav />
            <div className="py-3">
              <h1 className="title display-4 my-3">Stake Noundles</h1>
              <p className="lead">
                Please authorize the staking pool to access your noundles.
              </p>
              <button
                onClick={approve}
                disabled={loading}
                className="btn btn-primary text-white btn-lg px-3"
              >
                {loading ? (
                  <span
                    className="spinner-border me-2 align-bottom"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : (
                  ''
                )}
                Authorize staking pool
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Head>
        <title>Stake Noundles | Noundles</title>
      </Head>

      <div className="pink-gradient">
        <div className="container py-5">
          <StakeNav />
          {legacyBalance > 0 ? (
            <div className="py-3 text-center">
              <div className="alert alert-info">
                <div className="lead">
                  <div className="fw-bold">You have legacy Noundles.</div>
                  Please{' '}
                  <Link href="/stake/noundles/wrap" passHref>
                    <a className="alert-link">go here</a>
                  </Link>{' '}
                  to wrap them.
                </div>
              </div>
            </div>
          ) : (
            ''
          )}
          <div className="py-3 text-center">
            <h1 className="title display-4 my-3">Stake Noundles</h1>
            <div className="my-5">
              <UnstakedNoundlesPanel />
            </div>
            <hr />
            <div className="my-5">
              <StakedNoundlesPanel />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StakeNoundles;
